import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CafDecisionPrecedent, CafDecisionRow, CafDecisionTraceResponse, CafReplacementItem, getCafDecisionTrace, listCafTaskDecisions,
  recordCafDecisionReview,
} from '../../../api/cafCorpus';
import { VERDICT_COPY, Verdict } from '../copy';
import { formatWhen } from '../format';
import { Icon } from '../Icon';
import { ActionPill } from '../LabelForm';
import { senderEmail } from '../lenders';
import { Drawer } from '../Overlay';
import { newIdempotencyKey, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { aiStory, happenedSentence } from './decisionStory';
import { FixEmailModal } from './FixEmailModal';
import { DecisionEmailModal } from './DecisionEmailModal';

interface Props {
  row: CafDecisionRow;
  watched?: CafReplacementItem;
  lenderOf: (sender: string) => string | null;
  onClose: () => void;
  onChanged: () => void;
  onLabelSender: (sender: string) => void;
  onTeach: (row: CafDecisionRow, correction?: { decisionId: string; reviewRevision: number; feedbackOnly?: boolean }) => void;
  reviewRefreshKey?: number;
  onShowExampleHistory?: (precedent: CafDecisionPrecedent) => void;
  /** Controller note 9: this drawer is remounted fresh whenever the same row
   *  is closed and reopened (key={row.id}, see the comment below), so its own
   *  local "a fix may still be finishing" flag can't survive that alone.
   *  HowTab persists it across mounts with its own 2-minute TTL and passes it
   *  here; omitted (or undefined) falls back to this drawer's own state. */
  fixPending?: boolean;
  onFixPendingChange?: (pending: boolean) => void;
}

type Loaded =
  | { state: 'loading' }
  | { state: 'none' }
  | { state: 'ambiguous' }
  | { state: 'ready'; id: string; data: CafDecisionTraceResponse }
  | { state: 'failed' };

/**
 * Task 22: the story of one AI decision under four plain headings (What the
 * AI did / What it looked at / What happened / Was the AI right?), plus
 * feedback, Fix this email (Task 21) and Teach the AI from this email. The
 * drawer needs no feed flags -- a row without `decision_id` (older backend,
 * or no enrichment) is looked up by task (Review Focus #2).
 *
 * Mount with key={row.id}; state is not reset on a row change.
 */
export const DecisionDrawer: React.FC<Props> = ({
  row, watched, lenderOf, onClose, onChanged, onLabelSender, onTeach, onShowExampleHistory, reviewRefreshKey,
  fixPending: fixPendingProp, onFixPendingChange,
}) => {
  const toast = useToast();
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
  const [verdict, setVerdict] = useState<Verdict | ''>('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const [fixing, setFixing] = useState(false);
  const [readingEmail, setReadingEmail] = useState(false);
  // Controller note 9: once Fix this email closes WITHOUT confirming, the
  // correction may still be finishing server-side -- don't invite a second
  // one straight away. Cleared only by a confirmed fix (onFixed). Falls back
  // to this local flag when the caller doesn't track it (fixPendingProp unset).
  const [localFixPending, setLocalFixPending] = useState(false);
  const fixPending = fixPendingProp ?? localFixPending;
  // Final wave item 2 (ruling): the row a confirmed fix was made from. The feed is a beat
  // behind the fix, so until HowTab swaps in the reloaded row this drawer remembers the fix
  // itself: Fix is not offered again, the staff check reads "Fixed by staff", and the
  // pre-fix "What happened" sentence (now stale) is not shown.
  const [fixedFrom, setFixedFrom] = useState<CafDecisionRow | null>(null);
  const fixedHere = fixedFrom !== null;
  const staleAfterFix = fixedFrom === row && !row.overridden_at;
  const identity = useRef<{ content: string; key: string } | null>(null);

  const load = useCallback(async () => {
    setLoaded({ state: 'loading' });
    try {
      const decisions = row.decision_id ? null : await listCafTaskDecisions(row.id);
      if (decisions && (decisions.items.length > 1 || decisions.next_cursor)) {
        setLoaded({ state: 'ambiguous' });
        return;
      }
      const decisionId = row.decision_id ?? (decisions?.items.length === 1 && !decisions.next_cursor ? decisions.items[0]?.decision_id : null);
      if (!decisionId) {
        setLoaded({ state: 'none' });
        return;
      }
      setLoaded({ state: 'ready', id: decisionId, data: await getCafDecisionTrace(decisionId) });
    } catch {
      setLoaded({ state: 'failed' });
    }
  }, [row.decision_id, row.id, reviewRefreshKey]);

  useEffect(() => { void load(); }, [load]);

  const ready = loaded.state === 'ready' ? loaded : null;
  const story = aiStory(row, ready?.data.decision.trace ?? null);
  const reviews = ready?.data.reviews ?? null;
  const earlier = reviews ? [...reviews.items].reverse() : [];
  const canSave = Boolean(ready && verdict) && (verdict !== 'incorrect' || Boolean(note.trim())) && !saving;
  const sender = row.email_from || '';
  const proposedWorkflow = watched?.proposal.workflow as { name?: unknown } | undefined;
  const proposedPlan = watched?.proposal.plan as { ending?: unknown } | undefined;
  const firstPrecedent = row.precedents.find((item) => !item.missing && item.sender) ?? null;
  // Controller note 3: hidden once the decision has already been fixed.
  const canOfferFix = !watched && !row.overridden_at && !fixPending && !fixedHere;

  const save = async () => {
    if (!ready || !reviews || !verdict || !canSave) return;
    const content = JSON.stringify([ready.id, verdict, note, reviews.current_revision]);
    if (identity.current?.content !== content) identity.current = { content, key: newIdempotencyKey('caf-review') };
    const payload = {
      decision_id: ready.id, verdict, correction_text: note.trim(), source: 'staff_report' as const,
      expected_revision: reviews.current_revision, idempotency_key: identity.current.key,
    };
    setSaving(true);
    setProblem('');
    const result = await runSave(() => recordCafDecisionReview(row.id, payload), { replay: () => recordCafDecisionReview(row.id, payload) });
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      toast.failed(result.message, () => { void save(); });
      return;
    }
    identity.current = null;
    setVerdict('');
    setNote('');
    toast.saved('Saved.');
    onChanged();
    void load();
  };

  const copyDetails = () => {
    const trace = ready?.data.decision.trace;
    const details = {
      task: row.id, decision: ready?.id ?? null, handled: row.processed_at, subject: row.subject,
      provider: trace?.provider, model: trace?.model, prompt_version: trace?.prompt_version,
      policy_version: trace?.policy_version, config_version: trace?.config_version,
    };
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clipboard) {
      toast.info('Copying is blocked in this browser.');
      return;
    }
    clipboard.writeText(JSON.stringify(details, null, 2)).then(
      () => toast.saved('Copied. Paste it into your support message.'),
      () => toast.info('Copying is blocked in this browser.'),
    );
  };

  // Controller note 9, option B: a drawer-local flag set only when the fix
  // window closes without confirming -- FixEmailModal needs no new prop.
  // Reported to the caller so it survives this drawer's own remount.
  const closeFix = () => {
    setFixing(false);
    setLocalFixPending(true);
    onFixPendingChange?.(true);
  };

  const fixed = () => {
    setFixing(false);
    setFixedFrom(row);
    setLocalFixPending(false);
    onFixPendingChange?.(false);
    onChanged();
    void load();
  };

  return (
    <Drawer eyebrow="Decision" title={row.subject || '(No subject)'}
      subtitle={`${lenderOf(sender) ?? senderEmail(sender)} · ${senderEmail(sender)} · ${watched ? 'decided' : 'handled'} ${formatWhen(row.processed_at)}`}
      onClose={onClose} busy={saving}
      footer={(
        <>
          <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={() => setReadingEmail(true)}>
            Read email
          </button>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={copyDetails}>
            <Icon name="copy" size="sm" />Copy details for support
          </button>
          <span className="cur-spacer" />
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose}>Close</button>
        </>
      )}>
      <section>
        <h3 className="cur-sec-title">{watched ? 'What the AI proposed' : 'What the AI did'}</h3>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          {watched ? <strong>{watched.label}</strong> : <ActionPill action={story.action} past />}
          {story.sure != null && (
            <span className="cur-muted cur-small">How sure: {story.sure}%<span className="cur-meter" aria-hidden="true"><i style={{ width: `${story.sure}%` }} /></span></span>
          )}
        </div>
        {watched && <p className="cur-plain">Watching only: no action was taken.
          {watched.workflow_id && ` Proposed workflow ${typeof proposedWorkflow?.name === 'string' && proposedWorkflow.name ? proposedWorkflow.name : watched.workflow_id}.`}
          {typeof proposedPlan?.ending === 'string' && ` Proposed ending: ${proposedPlan.ending}.`}
        </p>}
        {story.said && <p className="cur-plain">{story.said}</p>}
        {!watched && story.change && (
          <p className="cur-plain" style={{ marginTop: 6 }}>
            The AI suggested <b>{story.change.from}</b>. A house rule changed it to <b>{story.change.to}</b>: {story.change.because}
          </p>
        )}
        {!watched && story.rule && <p className="cur-plain" style={{ marginTop: 6 }}>{story.rule}</p>}
      </section>
      {!watched && <section>
        <h3 className="cur-sec-title">What it looked at</h3>
        {row.precedents.length ? row.precedents.map((precedent) => (
          <div key={precedent.id} className="cur-ex-card">
            {precedent.missing ? <span className="cur-muted">An approved example that has since been removed.</span> : (
              <>
                <div className="top"><span className="cur-muted">Approved example</span> <ActionPill action={precedent.correct_action} /></div>
                <div className="cur-plain">{precedent.note}</div>
                {onShowExampleHistory && precedent.sender && (
                  <div><button type="button" className="cur-linkish cur-small" onClick={() => onShowExampleHistory(precedent)}>See this example&apos;s history</button></div>
                )}
              </>
            )}
          </div>
        )) : (
          <>
            <p className="cur-plain cur-muted">No approved example matched this sender.</p>
            {row.action === 'MANUAL_REVIEW' && sender && (
              <div style={{ marginTop: 8 }}>
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => onLabelSender(sender)}>
                  Label this sender&apos;s emails
                </button>
              </div>
            )}
          </>
        )}
      </section>}
      {!watched && <section>
        <h3 className="cur-sec-title">What happened</h3>
        {(row.overridden_at || fixedHere) && (
          <p className="cur-plain"><span className="cur-verdict cur-v-fixed"><Icon name="wrench" size="sm" />Fixed by staff</span></p>
        )}
        {!staleAfterFix && <p className="cur-plain">{happenedSentence(row)}</p>}
      </section>}
      <section>
        <h3 className="cur-sec-title">Was the AI right?</h3>
        {loaded.state === 'loading' && <p className="cur-hint">Loading…</p>}
        {loaded.state === 'failed' && (
          <>
            <p className="cur-hint">Couldn&apos;t load this decision.</p>
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { void load(); }}>Try again</button>
          </>
        )}
        {loaded.state === 'none' && <p className="cur-hint">This email was handled before decisions were recorded, so it can&apos;t be marked right or wrong.</p>}
        {loaded.state === 'ambiguous' && <p className="cur-hint">This email has more than one decision. Open a specific decision to give feedback.</p>}
        {ready && reviews && (
          <>
            <div className="cur-verdict-seg" role="group" aria-label="Was the AI right?">
              {(['correct', 'incorrect', 'unsure'] as Verdict[]).map((value) => (
                <button key={value} type="button" data-verdict={value} aria-pressed={verdict === value} onClick={() => {
                  if (value === 'incorrect') onTeach(row, { decisionId: ready.id, reviewRevision: reviews.current_revision, ...(row.archived ? { feedbackOnly: true } : {}) });
                  else setVerdict(value);
                }}>
                  <Icon name={VERDICT_COPY[value].icon} size="sm" />{VERDICT_COPY[value].label}
                </button>
              ))}
            </div>
            {verdict && <div className="cur-field" style={{ marginTop: 12 }}>
              <label htmlFor={`fb-note-${ready.id}`}>Note <span className="cur-opt">optional</span></label>
              <textarea id={`fb-note-${ready.id}`} className="cur-input" rows={2} maxLength={20000} value={note} disabled={saving}
                placeholder="Optional feedback note" onChange={(event) => setNote(event.target.value)} />
            </div>}
            {problem && <div className="cur-alert cur-alert--error" role="alert" style={{ marginTop: 8 }}>{problem}</div>}
            <div className="cur-btn-row" style={{ marginTop: 10 }}>
              {verdict && <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" disabled={!canSave} onClick={() => { void save(); }}>
                {saving ? 'Saving…' : 'Save feedback'}
              </button>}
              <span className="cur-spacer" />
              {canOfferFix && (
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => setFixing(true)}>
                  <Icon name="wrench" size="sm" />Fix this email…
                </button>
              )}
            </div>
            {fixPending && !row.overridden_at && !fixedHere && <p className="cur-hint">A fix may still be finishing. Refresh in a minute.</p>}
            {row.archived && <p className="cur-hint">Archived emails can be given feedback, but can&apos;t be used as examples until restored.</p>}
          </>
        )}
        {/* A standalone teach action is only needed when there is no usable
            decision identity for feedback. Ready decisions use Wrong above. */}
        {loaded.state !== 'loading' && (!ready || !reviews) && (
          <div className="cur-teach-one">
            {row.archived ? (
              <p className="cur-hint">Archived emails can&apos;t be used as examples. Use Fix this email to bring it back first.</p>
            ) : (
              <>
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => onTeach(row, ready && reviews ? { decisionId: ready.id, reviewRevision: reviews.current_revision } : undefined)}>
                  <Icon name="bolt" size="sm" />Teach the AI from this email
                </button>
                <span className="cur-hint">Makes this email an approved example.</span>
              </>
            )}
          </div>
        )}
        {ready && reviews && earlier.length > 0 && (
          <>
            <h4 className="cur-sub-h" style={{ padding: '16px 0 8px' }}>Earlier feedback</h4>
            <ul className="cur-fb-list">
              {earlier.map((review) => (
                <li key={review.review_id}>
                  <span className={`cur-verdict ${VERDICT_COPY[review.verdict].className}`}>
                    <Icon name={VERDICT_COPY[review.verdict].icon} size="sm" />{VERDICT_COPY[review.verdict].label}
                  </span>{' '}
                  <span className="cur-muted cur-small">{review.actor.email} · {formatWhen(review.created_at)}</span>
                  {review.correction_text && <div style={{ marginTop: 3 }}>{review.correction_text}</div>}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
      {fixing && ready && reviews && (
        <FixEmailModal row={row} decisionId={ready.id} reviews={reviews} precedent={firstPrecedent}
          onClose={closeFix}
          onFixed={fixed} />
      )}
      {readingEmail && <DecisionEmailModal taskId={row.id} onClose={() => setReadingEmail(false)} />}
    </Drawer>
  );
};
